// ═══════════════════════════════════════════════════════════════
// GeoOps — Zone Detail with map overlays, scan, cameras,
// weather, staging, evacuation, AI briefing
// ═══════════════════════════════════════════════════════════════

import { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import {
  Title, Text, Paper, Group, Badge, Button, Stack, SimpleGrid,
  Center, ThemeIcon, Tabs, Table, Loader, ScrollArea, Box,
} from '@mantine/core';
import {
  IconArrowLeft, IconAlertTriangle, IconMapPin, IconEdit, IconRadar,
  IconRoadSign, IconCamera, IconCloudStorm, IconBrain,
  IconRoute, IconFlag, IconRefresh, IconBuilding, IconPhone, IconMail,
  IconDownload,
} from '@tabler/icons-react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  MapContainer, TileLayer, LayersControl, CircleMarker, Marker, Popup,
} from 'react-leaflet';
import * as L from 'leaflet';
import Hls from 'hls.js';
import { notifications } from '@mantine/notifications';
import { useGeoOpsStore, ZONE_TYPE_META } from '../../store/geoopsStore';
import type { GeoOpsZone } from '../../store/geoopsStore';
import { useModulePermission } from '../../hooks/useModulePermission';
import ZoneMapOverlay from '../../components/map/ZoneMapOverlay';
import IncidentLocationMarker from '../../components/map/IncidentLocationMarker';
import AutoRefreshImage from '../../components/map/AutoRefreshImage';
import ZoneForm from './ZoneForm';
import { polygonAreaSqMi, geometryCentroid, zoneBounds, haversineDistance } from '../../lib/geoopsGeo';
import { useAuthStore } from '../../store/authStore';
import { formatPhone } from '../../lib/phoneFormat';

import { scanPOIs, scanIntersections } from '../../utils/perimeterOverpass';
import { fetchCamerasNearby, fetchCameraStream, fetchGISFeatures } from '../../utils/perimeterCameras';
import { useGeoOpsDataStore } from '../../store/geoopsDataStore';
import { fetchWeather } from '../../utils/perimeterWeather';
import { analyzeEvacuationRoutes, suggestStagingAreas } from '../../utils/perimeterEvacuation';
import { PRIORITY_LABELS, PRIORITY_COLORS, DIR_LABELS } from '../../utils/perimeterConstants';
import type { PerimeterPOI, PerimeterClosure } from '../../utils/perimeterGeo';
import { primary, semantic } from '../../lib/colors';
import { callClaude } from '../../lib/api-switch';
import { useMainStreetStore } from '../../store/mainstreetStore';
import { SECTOR_COLORS } from '../mainstreet/constants';
import { isInsideZone } from '../../lib/geoopsGeo';
import Breadcrumbs from '../../components/ui/Breadcrumbs';

import 'leaflet/dist/leaflet.css';
import { useMap } from 'react-leaflet';

function FitToZone({ geometry }: { geometry: GeoJSON.Geometry | null }) {
  const map = useMap();
  useEffect(() => {
    if (!geometry) return;
    const bounds = zoneBounds(geometry);
    if (bounds) map.fitBounds(bounds, { padding: [40, 40] });
  }, [geometry, map]);
  return null;
}

function computeBoundingRadius(geometry: GeoJSON.Geometry, centroid: [number, number]): number {
  let maxDist = 0;
  const coords: number[][] = [];
  if (geometry.type === 'Polygon') coords.push(...geometry.coordinates[0]);
  else if (geometry.type === 'LineString') coords.push(...geometry.coordinates);
  else return 0.5;
  for (const [lng, lat] of coords) {
    const d = haversineDistance(centroid[0], centroid[1], lat, lng);
    if (d > maxDist) maxDist = d;
  }
  return Math.max(0.1, (maxDist / 1609.34) * 1.1);
}

// Camera icon factory
function camIcon(selected: boolean) {
  return L.divIcon({
    className: '',
    html: `<div style="width:${selected ? 28 : 22}px;height:${selected ? 28 : 22}px;background:${selected ? '#00e5ff' : '#00bcd4'};border:2px solid ${selected ? '#fff' : 'rgba(255,255,255,0.8)'};border-radius:4px;display:flex;align-items:center;justify-content:center;box-shadow:0 2px 6px rgba(0,0,0,0.4);${selected ? 'z-index:1000;' : ''}"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M15 10l4.553 -2.276A1 1 0 0 1 21 8.618v6.764a1 1 0 0 1 -1.447 .894L15 14v-4z"/><rect x="2" y="6" width="13" height="12" rx="2"/></svg></div>`,
    iconSize: [selected ? 28 : 22, selected ? 28 : 22],
    iconAnchor: [selected ? 14 : 11, selected ? 14 : 11],
  });
}

const SUITABILITY_COLORS: Record<string, string> = { high: 'green', medium: 'yellow', low: 'red' };

export default function ZoneDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { zones, loadZones, updateZone } = useGeoOpsStore();
  const { activeIncident } = useAuthStore();
  const { canEdit } = useModulePermission('geoops');
  const { cameras: cameraSources, loadCameras, gisLayers, loadGISLayers } = useGeoOpsDataStore();
  const [showEdit, setShowEdit] = useState(false);

  // Business layer
  const { organizations, loadOrganizations } = useMainStreetStore();
  useEffect(() => { loadOrganizations(); loadCameras(); loadGISLayers(); }, []);

  // Scan state
  const [pois, setPois] = useState<PerimeterPOI[]>([]);
  const [closures, setClosures] = useState<PerimeterClosure[]>([]);
  const [cameras, setCameras] = useState<any[]>([]);
  const [weather, setWeather] = useState<any>(null);
  const [briefingText, setBriefingText] = useState('');
  const [scanning, setScanning] = useState(false);
  const [scanProgress, setScanProgress] = useState('');
  const [briefingLoading, setBriefingLoading] = useState(false);
  const [scanned, setScanned] = useState(false);
  const [scanTimestamp, setScanTimestamp] = useState<string>('');

  // Camera viewer state
  const [viewingCamera, setViewingCamera] = useState<any>(null);
  const [selectedCamera, setSelectedCamera] = useState<any>(null);
  const [streamUrl, setStreamUrl] = useState<string | null>(null);
  const [streamLoading, setStreamLoading] = useState(false);
  const [streamMode, setStreamMode] = useState<'video' | 'snapshot'>('video');
  const [camImageTick, setCamImageTick] = useState(0);
  const videoRef = useRef<HTMLVideoElement>(null);
  const hlsRef = useRef<Hls | null>(null);

  // Map pan ref
  const mapRef = useRef<L.Map | null>(null);

  useEffect(() => {
    if (activeIncident && zones.length === 0) loadZones(String(activeIncident.id));
  }, [activeIncident]);

  const zone = useMemo(() => zones.find(z => String(z.id) === String(id)), [zones, id]);

  // ── Restore saved scan data from localStorage on load ─────
  // Scan results are cached client-side keyed by zone ID. This avoids
  // Ignatius's TEXT field limits and the 500 errors we were getting
  // trying to write nested JSON to the metadata column.
  useEffect(() => {
    if (!zone) return;
    try {
      const raw = localStorage.getItem(`geoops-scan-${zone.id}`);
      if (!raw) return;
      const saved = JSON.parse(raw) as {
        pois?: PerimeterPOI[];
        closures?: PerimeterClosure[];
        cameras?: any[];
        weather?: any;
        scannedAt?: string;
      };
      if (saved.pois?.length || saved.closures?.length || saved.cameras?.length) {
        setPois(saved.pois || []);
        setClosures(saved.closures || []);
        setCameras(saved.cameras || []);
        if (saved.weather) setWeather(saved.weather);
        if (saved.scannedAt) setScanTimestamp(saved.scannedAt);
        setScanned(true);
        console.log('[GeoOps] Restored scan cache:', saved.pois?.length, 'POIs,', saved.closures?.length, 'closures,', saved.cameras?.length, 'cameras');
      }
    } catch { /* corrupt cache — ignore, user can re-scan */ }
  }, [zone?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Camera stream management
  useEffect(() => {
    if (!viewingCamera) {
      setStreamUrl(null);
      setStreamMode('video');
      if (hlsRef.current) { hlsRef.current.destroy(); hlsRef.current = null; }
      return;
    }
    let cancelled = false;
    setStreamLoading(true);
    fetchCameraStream(viewingCamera).then(url => {
      if (cancelled) return;
      setStreamUrl(url);
      setStreamLoading(false);
      if (!url) setStreamMode('snapshot');
    }).catch(() => {
      if (!cancelled) { setStreamUrl(null); setStreamLoading(false); setStreamMode('snapshot'); }
    });
    return () => { cancelled = true; };
  }, [viewingCamera]);

  useEffect(() => {
    if (!streamUrl || streamMode !== 'video' || !videoRef.current) return;
    const video = videoRef.current;
    if (hlsRef.current) { hlsRef.current.destroy(); hlsRef.current = null; }
    if (Hls.isSupported()) {
      const hls = new Hls({ enableWorker: false, lowLatencyMode: true });
      hlsRef.current = hls;
      hls.loadSource(streamUrl);
      hls.attachMedia(video);
      hls.on(Hls.Events.MANIFEST_PARSED, () => { video.play().catch(() => {}); });
      hls.on(Hls.Events.ERROR, (_e, data) => { if (data.fatal) setStreamMode('snapshot'); });
    } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
      video.src = streamUrl;
      video.addEventListener('loadedmetadata', () => { video.play().catch(() => {}); });
    } else {
      setStreamMode('snapshot');
    }
    return () => { if (hlsRef.current) { hlsRef.current.destroy(); hlsRef.current = null; } };
  }, [streamUrl, streamMode]);

  useEffect(() => {
    if (!viewingCamera || streamMode !== 'video') return;
    const interval = setInterval(() => {
      fetchCameraStream(viewingCamera).then(url => {
        if (url && hlsRef.current) hlsRef.current.loadSource(url);
      }).catch(() => {});
    }, 90000);
    return () => clearInterval(interval);
  }, [viewingCamera, streamMode]);

  useEffect(() => {
    if (!viewingCamera || streamMode !== 'snapshot') return;
    const interval = setInterval(() => setCamImageTick(t => t + 1), 5000);
    return () => clearInterval(interval);
  }, [viewingCamera, streamMode]);

  if (!zone) {
    return (
      <div style={{ padding: 24 }}>
        <Button variant="subtle" size="xs" leftSection={<IconArrowLeft size={14} />}
          onClick={() => navigate('/geoops')} mb="md">Back</Button>
        <Center py={80}>
          <Stack align="center" gap="sm">
            <ThemeIcon size={60} radius="xl" variant="light" color="gray">
              <IconAlertTriangle size={30} />
            </ThemeIcon>
            <Title order={3} c="black">Zone Not Found</Title>
            <Text size="sm" c="dimmed">This zone may have been deleted.</Text>
          </Stack>
        </Center>
      </div>
    );
  }

  const meta = ZONE_TYPE_META[zone.zoneType] || ZONE_TYPE_META.custom;
  const area = zone.geometry ? polygonAreaSqMi(zone.geometry) : 0;
  const centroid = zone.geometry ? geometryCentroid(zone.geometry) : null;
  const boundingRadius = zone.geometry && centroid ? computeBoundingRadius(zone.geometry, centroid) : 1;

  const evacuationRoutes = useMemo(
    () => closures.length > 0 ? analyzeEvacuationRoutes(closures) : [],
    [closures],
  );
  const stagingAreas = useMemo(
    () => centroid && pois.length > 0
      ? suggestStagingAreas(pois, { lat: centroid[0], lng: centroid[1] }, boundingRadius)
      : [],
    [pois, centroid, boundingRadius],
  );

  const poiGroups = useMemo(() => {
    const groups: Record<string, PerimeterPOI[]> = {};
    pois.forEach(p => {
      const key = p.categoryLabel || p.category || 'Other';
      if (!groups[key]) groups[key] = [];
      groups[key].push(p);
    });
    return groups;
  }, [pois]);

  const panTo = (lat: number, lng: number, zoom?: number) => {
    mapRef.current?.setView([lat, lng], zoom ?? mapRef.current.getZoom(), { animate: true });
  };

  // ── Scan handler ──────────────────────────────────────────
  const handleScan = useCallback(async () => {
    if (!centroid || !zone.geometry) return;
    setScanning(true);
    setScanned(false);
    const center = { lat: centroid[0], lng: centroid[1] };
    try {
      setScanProgress('Scanning points of interest...');
      const p = await scanPOIs(center, boundingRadius, msg => setScanProgress(msg));
      setPois(p);
      setScanProgress('Detecting road intersections...');
      const c = await scanIntersections(center, boundingRadius, msg => setScanProgress(msg));
      setClosures(c);
      setScanProgress('Finding traffic cameras...');
      const cams = await fetchCamerasNearby(center, boundingRadius, cameraSources);
      // Also fetch features from GIS layers and merge as extra POIs
      if (gisLayers.filter(g => g.status === 'active').length > 0) {
        setScanProgress('Fetching GIS layer features...');
        const gisResults = await Promise.all(
          gisLayers.filter(g => g.status === 'active').map(g =>
            fetchGISFeatures(g).catch(() => [])
          )
        );
        const gisFlat = gisResults.flat();
        // Add GIS features as POIs in the existing POI list
        const gisPOIs = gisFlat
          .filter(f => isInsideZone(zone.geometry as GeoJSON.Geometry, f.lat, f.lng))
          .map((f, i) => ({
            id: -(i + 1),
            name: f.name,
            category: f.category,
            lat: f.lat,
            lng: f.lng,
            distance: 0,
            type: f.category,
            address: '',
            phone: '',
            source: `GIS: ${f.sourceName}`,
          } as PerimeterPOI));
        setPois(prev => [...prev, ...gisPOIs]);
      }
      setCameras(cams);
      setScanProgress('Fetching weather...');
      const wx = await fetchWeather(centroid[0], centroid[1]);
      setWeather(wx);
      setScanned(true);
      setScanProgress('');
      const now = new Date().toISOString();
      setScanTimestamp(now);

      // Cache scan results in localStorage so they persist across page
      // navigations without re-scanning. We tried saving to Ignatius's
      // metadata field but it 500'd on nested JSON. localStorage has no
      // such limits (~5-10MB per origin) and scan data is ephemeral cache.
      try {
        localStorage.setItem(`geoops-scan-${zone.id}`, JSON.stringify({
          pois: p, closures: c, cameras: cams, weather: wx, scannedAt: now,
        }));
      } catch { /* localStorage full or unavailable — not critical */ }

      notifications.show({ message: `Scan complete: ${p.length} POIs, ${c.length} closures, ${cams.length} cameras`, color: 'green' });
    } catch (err) {
      console.error('Scan error:', err);
      notifications.show({ message: 'Scan failed', color: 'red' });
      setScanProgress('');
    } finally {
      setScanning(false);
    }
  }, [centroid, boundingRadius, zone.geometry]);

  // ── Briefing handler ──────────────────────────────────────
  const handleGenerateBriefing = useCallback(async () => {
    if (!centroid) return;
    setBriefingLoading(true);
    try {
      const poiSummary = pois.length > 0
        ? pois.slice(0, 30).map(p => `- ${p.categoryLabel || p.category}: ${p.name} (${p.distance.toFixed(2)} mi)`).join('\n')
        : 'No POIs identified.';
      const closureSummary = closures.length > 0
        ? closures.slice(0, 20).map(c => `- ${c.name}: ${c.direction} ${c.roadType}, priority ${c.priority} (${c.distance.toFixed(2)} mi)`).join('\n')
        : 'No road closures identified.';
      const prompt = `Generate an operational briefing using ONLY the data below.\n\nZONE: ${zone.name} (${meta.label}, ${zone.status})\nArea: ${area > 0.01 ? area.toFixed(2) + ' sq mi' : (area * 640).toFixed(1) + ' acres'}\nCameras: ${cameras.length}\n${weather ? `Weather: ${weather.temp}°F, ${weather.description}, Wind ${weather.windSpeed} mph` : ''}\n\nPOIs (${pois.length}):\n${poiSummary}\n\nROAD INTERSECTIONS (${closures.length}):\n${closureSummary}\n\nSections: 1. Situation Overview 2. Population Impact 3. Access & Transportation 4. Key Facilities 5. Staging Recommendations 6. Action Items\n\nIMPORTANT: Only reference data above.`;
      const text = await callClaude([{ role: 'user', content: prompt }], {
        system: 'You are an emergency management zone analysis tool. Generate concise operational briefings using ONLY the data provided. Never invent names or locations.',
        maxTokens: 2048,
      });
      setBriefingText(text);
    } catch { notifications.show({ message: 'Failed to generate briefing', color: 'red' }); }
    finally { setBriefingLoading(false); }
  }, [pois, closures, cameras, weather, zone, centroid, area, meta]);

  const handleEditSave = async (formData: {
    name: string; zoneType: string; status: string;
    color: string; opacity: number; notes: string;
  }) => {
    await updateZone(zone.id, {
      ...formData,
      zoneType: formData.zoneType as GeoOpsZone['zoneType'],
      status: formData.status as GeoOpsZone['status'],
    });
    setShowEdit(false);
  };

  const handleSelectCamera = (cam: any) => {
    setSelectedCamera(cam);
    setViewingCamera(cam);
  };

  return (
    <div style={{ padding: '12px 24px' }}>
      {/* Header */}
      <Breadcrumbs items={[
        { label: 'GeoOps', to: '/geoops' },
        { label: zone.name },
      ]} />
      <Group justify="space-between" mb="sm">
        <Group gap="sm">
          <div style={{ width: 14, height: 14, borderRadius: 3, backgroundColor: zone.color || meta.color }} />
          <Title order={3} c="black">{zone.name}</Title>
          <Badge size="sm" color={meta.mantine}>{meta.label}</Badge>
          <Badge size="sm" variant="dot" color={zone.status === 'active' ? 'green' : zone.status === 'planned' ? 'yellow' : 'gray'}>
            {zone.status}
          </Badge>
        </Group>
        <Group gap="xs">
          <Button variant="filled" size="xs" leftSection={<IconRadar size={14} />}
            onClick={handleScan} loading={scanning}>
            {scanned ? 'Re-Scan' : 'Scan Zone'}
          </Button>
          {scanned && (
            <>
              <Button variant="light" size="xs" leftSection={<IconDownload size={14} />}
                onClick={() => {
                  const esc = (v: string) => `"${(v || '').replace(/"/g, '""')}"`;
                  const rows = [
                    ['Type', 'Name', 'Category', 'Address', 'Phone', 'Distance (mi)', 'Lat', 'Lng'].join(','),
                    ...pois.map(p => [esc('POI'), esc(p.name), esc(p.categoryLabel || ''), esc(p.address || ''), esc(p.phone || ''), p.distance?.toFixed(2) || '', String(p.lat), String(p.lng)].join(',')),
                    ...closures.map(c => [esc('Intersection'), esc(c.name), esc(c.roadType || ''), esc(c.streets?.join(' x ') || ''), '', c.distance?.toFixed(2) || '', String(c.lat), String(c.lng)].join(',')),
                    ...cameras.map(c => [esc('Camera'), esc(c.name || c.location || ''), esc(c.roadway || ''), '', '', c.distance?.toFixed(2) || '', String(c.lat), String(c.lng)].join(',')),
                  ];
                  const blob = new Blob([rows.join('\n')], { type: 'text/csv' });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `${(zone.name || 'zone').replace(/[^a-z0-9]+/gi, '_')}_scan_${new Date().toISOString().split('T')[0]}.csv`;
                  a.click();
                  URL.revokeObjectURL(url);
                  notifications.show({ message: 'CSV exported', color: 'green' });
                }}>Export CSV</Button>
              <Button variant="light" size="xs" leftSection={<IconDownload size={14} />}
                onClick={() => {
                  const printW = window.open('', '_blank');
                  if (!printW) return;
                  const poiRows = pois.map(p => `<tr><td>${p.categoryLabel || ''}</td><td>${p.name}</td><td>${p.address || ''}</td><td>${p.phone || ''}</td><td>${p.distance?.toFixed(2) || ''}</td></tr>`).join('');
                  const closureRows = closures.map(c => `<tr><td>${c.name}</td><td>${c.streets?.join(' x ') || ''}</td><td>${c.roadType || ''}</td><td>P${c.priority || '?'}</td><td>${c.distance?.toFixed(2) || ''}</td></tr>`).join('');
                  const camRows = cameras.map(c => `<tr><td>${c.name || c.location || ''}</td><td>${c.roadway || ''}</td><td>${c.direction || ''}</td><td>${c.distance?.toFixed(2) || ''}</td></tr>`).join('');
                  printW.document.write(`<html><head><title>${zone.name} — Scan Report</title>
                    <style>body{font-family:Arial,sans-serif;padding:24px;font-size:12px}table{width:100%;border-collapse:collapse;margin:12px 0}th,td{border:1px solid #ccc;padding:5px 7px;text-align:left}th{background:#f5f5f5;font-weight:700}h1{font-size:18px}h2{font-size:14px;margin-top:16px}@media print{body{padding:0}}</style>
                    </head><body>
                    <h1>${zone.name} — Zone Scan Report</h1>
                    <p>Type: ${meta.label} | Status: ${zone.status} | Area: ${area > 0.01 ? area.toFixed(2) + ' sq mi' : (area * 640).toFixed(1) + ' acres'}${scanTimestamp ? ` | Scanned: ${new Date(scanTimestamp).toLocaleString()}` : ''}</p>
                    ${weather ? `<p>Weather: ${weather.temp}°F, ${weather.description}, Wind ${weather.windSpeed} mph</p>` : ''}
                    <h2>Points of Interest (${pois.length})</h2>
                    ${pois.length ? `<table><thead><tr><th>Category</th><th>Name</th><th>Address</th><th>Phone</th><th>Distance</th></tr></thead><tbody>${poiRows}</tbody></table>` : '<p>No POIs found.</p>'}
                    <h2>Road Intersections (${closures.length})</h2>
                    ${closures.length ? `<table><thead><tr><th>Intersection</th><th>Streets</th><th>Road Type</th><th>Priority</th><th>Distance</th></tr></thead><tbody>${closureRows}</tbody></table>` : '<p>No intersections found.</p>'}
                    <h2>Traffic Cameras (${cameras.length})</h2>
                    ${cameras.length ? `<table><thead><tr><th>Camera</th><th>Roadway</th><th>Direction</th><th>Distance</th></tr></thead><tbody>${camRows}</tbody></table>` : '<p>No cameras found.</p>'}
                    <p style="margin-top:24px;color:#888;font-size:10px">Generated by OpenGov EMC — GeoOps</p>
                    </body></html>`);
                  printW.document.close();
                  printW.focus();
                  printW.print();
                }}>Print / PDF</Button>
            </>
          )}
          {canEdit && (
            <Button variant="light" size="xs" leftSection={<IconEdit size={14} />}
              onClick={() => setShowEdit(true)}>Edit</Button>
          )}
        </Group>
      </Group>

      {scanTimestamp && scanned && !scanning && (
        <Text size="xs" c="dimmed" mb="xs">
          Last scanned: {new Date(scanTimestamp).toLocaleString()}
          {pois.length > 0 && ` — ${pois.length} POIs, ${closures.length} intersections, ${cameras.length} cameras`}
        </Text>
      )}

      {scanning && scanProgress && (
        <Paper p="xs" mb="xs" withBorder style={{ borderColor: '#3B82F6' }}>
          <Group gap="xs"><Loader size="xs" /><Text size="sm" c="blue">{scanProgress}</Text></Group>
        </Paper>
      )}

      {/* Map with overlays */}
      <Paper withBorder style={{ height: 380, overflow: 'hidden', borderRadius: 8 }} mb="sm">
        <MapContainer center={centroid || [33.749, -84.388]} zoom={13}
          style={{ height: '100%', width: '100%' }}
          ref={(m) => { if (m) mapRef.current = m; }}>
          <LayersControl position="topright">
            <LayersControl.BaseLayer checked name="Street">
              <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution='&copy; OpenStreetMap' />
            </LayersControl.BaseLayer>
            <LayersControl.BaseLayer name="Satellite">
              <TileLayer url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}" attribution='&copy; Esri' />
            </LayersControl.BaseLayer>
            <LayersControl.BaseLayer name="Dark">
              <TileLayer url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png" attribution='&copy; CARTO' />
            </LayersControl.BaseLayer>
          </LayersControl>

          <ZoneMapOverlay zones={[zone]} interactive={false} />

          {/* Active incident location pin */}
          <IncidentLocationMarker incident={activeIncident} />
          <FitToZone geometry={zone.geometry} />

          {/* POI markers */}
          {pois.map(poi => (
            <CircleMarker key={poi.id} center={[poi.lat, poi.lng]} radius={5}
              pathOptions={{ color: primary[700], fillColor: primary[700], fillOpacity: 0.8, weight: 1 }}>
              <Popup>
                <div style={{ fontSize: 13 }}>
                  <strong>{poi.categoryIcon} {poi.name}</strong><br />
                  <span style={{ color: '#666' }}>{poi.categoryLabel}</span><br />
                  {poi.address && <><span>{poi.address}</span><br /></>}
                  <span style={{ fontFamily: 'monospace', fontSize: 11 }}>{poi.distance.toFixed(2)} mi</span>
                </div>
              </Popup>
            </CircleMarker>
          ))}

          {/* Closure markers */}
          {closures.map(c => (
            <CircleMarker key={c.id} center={[c.lat, c.lng]} radius={6}
              pathOptions={{
                color: (PRIORITY_COLORS as any)[c.priority] || '#E63946',
                fillColor: (PRIORITY_COLORS as any)[c.priority] || '#E63946',
                fillOpacity: 0.9, weight: 2,
              }}>
              <Popup>
                <div style={{ fontSize: 13 }}>
                  <strong style={{ color: semantic.error }}>&#x2716; {c.name}</strong><br />
                  {c.direction} — {c.distance.toFixed(2)} mi<br />
                  <span style={{ color: '#666' }}>{c.roadType} · {(PRIORITY_LABELS as any)[c.priority]}</span>
                </div>
              </Popup>
            </CircleMarker>
          ))}

          {/* Camera markers */}
          {cameras.map((cam, idx) => {
            const isSelected = selectedCamera?.id === cam.id;
            return (
              <Marker key={cam.id || `cam-${idx}`} position={[cam.lat, cam.lng]}
                icon={camIcon(isSelected)}
                eventHandlers={{ click: () => handleSelectCamera(cam) }}>
                <Popup>
                  <div style={{ fontSize: 13 }}>
                    <strong>📹 {cam.name || 'Camera'}</strong><br />
                    {cam.distance?.toFixed(2)} mi · {cam.source}
                  </div>
                </Popup>
              </Marker>
            );
          })}

          {/* Business markers */}
          {organizations.map(org => {
            if (!org.coordinates) return null;
            const parts = org.coordinates.split(',').map(s => parseFloat(s.trim()));
            if (parts.length < 2 || isNaN(parts[0]) || isNaN(parts[1])) return null;
            const inZone = zone.geometry ? isInsideZone(parts[0], parts[1], zone.geometry) : false;
            const color = SECTOR_COLORS[org.sectorType] || '#0891b2';
            return (
              <CircleMarker key={`biz-${org.id}`} center={[parts[0], parts[1]]} radius={7}
                pathOptions={{
                  fillColor: color, fillOpacity: 0.9, weight: inZone ? 3 : 1.5,
                  color: inZone ? '#DC2626' : '#fff',
                }}>
                <Popup minWidth={220}>
                  <div style={{ fontFamily: '"DM Sans", sans-serif', fontSize: 13 }}>
                    <Text size="sm" fw={700} mb={2}>{org.name}</Text>
                    {org.sectorType && <Badge size="xs" variant="light" color="cyan" mb={4}>{org.sectorType}</Badge>}
                    {inZone && <Badge size="xs" color="red" variant="light" ml={4} mb={4}>In Zone</Badge>}
                    {org.primaryContact && (
                      <Group gap={4} mb={2}><IconBuilding size={12} color="#888" /><Text size="xs">{org.primaryContact}</Text></Group>
                    )}
                    {org.phone && (
                      <Group gap={4} mb={2}><IconPhone size={12} color="#888" /><Text size="xs" component="a" href={`tel:${org.phone}`} c="blue">{formatPhone(org.phone)}</Text></Group>
                    )}
                    {org.email && (
                      <Group gap={4} mb={2}><IconMail size={12} color="#888" /><Text size="xs" component="a" href={`mailto:${org.email}`} c="blue">{org.email}</Text></Group>
                    )}
                    {org.address && <Text size="xs" c="dimmed">{org.address}{org.city ? `, ${org.city}` : ''}</Text>}
                    <Badge size="xs" variant="light" mt={4}
                      color={org.opStatus === 'operational' ? 'green' : org.opStatus === 'limited' ? 'yellow' : 'red'}>
                      {org.opStatus || 'unknown'}
                    </Badge>
                  </div>
                </Popup>
              </CircleMarker>
            );
          })}
        </MapContainer>
      </Paper>

      {/* Zone info strip */}
      <SimpleGrid cols={{ base: 2, sm: 4, md: 6 }} mb="sm" spacing="xs">
        <Paper p="xs" withBorder ta="center">
          <Text size="xs" c="dimmed" tt="uppercase">Area</Text>
          <Text fw={700}>{area > 0.01 ? `${area.toFixed(2)} sq mi` : area > 0 ? `${(area * 640).toFixed(1)} ac` : '—'}</Text>
        </Paper>
        <Paper p="xs" withBorder ta="center">
          <Text size="xs" c="dimmed" tt="uppercase">POIs</Text>
          <Text fw={700}>{pois.length}</Text>
        </Paper>
        <Paper p="xs" withBorder ta="center">
          <Text size="xs" c="dimmed" tt="uppercase">Closures</Text>
          <Text fw={700}>{closures.length}</Text>
        </Paper>
        <Paper p="xs" withBorder ta="center">
          <Text size="xs" c="dimmed" tt="uppercase">Cameras</Text>
          <Text fw={700}>{cameras.length}</Text>
        </Paper>
        <Paper p="xs" withBorder ta="center">
          <Text size="xs" c="dimmed" tt="uppercase">Staging</Text>
          <Text fw={700}>{stagingAreas.length}</Text>
        </Paper>
        <Paper p="xs" withBorder ta="center">
          <Text size="xs" c="dimmed" tt="uppercase">Evac Routes</Text>
          <Text fw={700}>{evacuationRoutes.length}</Text>
        </Paper>
      </SimpleGrid>

      {/* Tabs */}
      {scanned && (
        <Tabs defaultValue="pois">
          <Tabs.List mb="xs">
            <Tabs.Tab value="pois" leftSection={<IconMapPin size={14} />}>POIs</Tabs.Tab>
            <Tabs.Tab value="closures" leftSection={<IconRoadSign size={14} />}>Closures</Tabs.Tab>
            <Tabs.Tab value="cameras" leftSection={<IconCamera size={14} />}>Cameras</Tabs.Tab>
            <Tabs.Tab value="staging" leftSection={<IconFlag size={14} />}>Staging</Tabs.Tab>
            <Tabs.Tab value="evacuation" leftSection={<IconRoute size={14} />}>Evacuation</Tabs.Tab>
            <Tabs.Tab value="weather" leftSection={<IconCloudStorm size={14} />}>Weather</Tabs.Tab>
            <Tabs.Tab value="briefing" leftSection={<IconBrain size={14} />}>AI Briefing</Tabs.Tab>
          </Tabs.List>

          {/* POIs */}
          <Tabs.Panel value="pois">
            <ScrollArea h={400}>
              {Object.entries(poiGroups).map(([cat, items]) => (
                <div key={cat}>
                  <Text size="xs" fw={700} tt="uppercase" c="dimmed" mb={4} mt="sm">{cat} ({items.length})</Text>
                  <Table striped highlightOnHover>
                    <Table.Tbody>
                      {items.map((poi, i) => (
                        <Table.Tr key={i} style={{ cursor: 'pointer' }} onClick={() => panTo(poi.lat, poi.lng, 16)}>
                          <Table.Td w={30}>{poi.categoryIcon}</Table.Td>
                          <Table.Td>
                            <Text size="sm" fw={500}>{poi.name}</Text>
                            <Text size="xs" c="dimmed">{poi.address}</Text>
                          </Table.Td>
                          <Table.Td w={80} ta="right"><Text size="xs" c="dimmed">{poi.distance.toFixed(2)} mi</Text></Table.Td>
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </Table>
                </div>
              ))}
              {pois.length === 0 && <Text size="sm" c="dimmed" ta="center" py="xl">No POIs found.</Text>}
            </ScrollArea>
          </Tabs.Panel>

          {/* Closures */}
          <Tabs.Panel value="closures">
            <ScrollArea h={400}>
              <Table striped highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Intersection</Table.Th>
                    <Table.Th>Direction</Table.Th>
                    <Table.Th>Type</Table.Th>
                    <Table.Th>Priority</Table.Th>
                    <Table.Th>Distance</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {closures.map((c, i) => (
                    <Table.Tr key={i} style={{ cursor: 'pointer' }} onClick={() => panTo(c.lat, c.lng, 16)}>
                      <Table.Td><Text size="sm">{c.name}</Text></Table.Td>
                      <Table.Td><Badge size="xs" variant="light">{c.direction}</Badge></Table.Td>
                      <Table.Td><Text size="xs">{c.roadType}</Text></Table.Td>
                      <Table.Td>
                        <Badge size="xs" style={{
                          backgroundColor: ((PRIORITY_COLORS as any)[c.priority] || '#888') + '20',
                          color: (PRIORITY_COLORS as any)[c.priority] || '#888',
                        }}>{(PRIORITY_LABELS as any)[c.priority] || `P${c.priority}`}</Badge>
                      </Table.Td>
                      <Table.Td><Text size="xs" c="dimmed">{c.distance.toFixed(2)} mi</Text></Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
              {closures.length === 0 && <Text size="sm" c="dimmed" ta="center" py="xl">No closures detected.</Text>}
            </ScrollArea>
          </Tabs.Panel>

          {/* Cameras */}
          <Tabs.Panel value="cameras">
            {viewingCamera ? (
              <div>
                <Group gap={8} mb={8} justify="space-between">
                  <Group gap={8}>
                    <Button size="compact-xs" variant="light" leftSection={<IconArrowLeft size={12} />}
                      onClick={() => { setViewingCamera(null); setSelectedCamera(null); }}>Back</Button>
                    <Text size="sm" fw={700} lineClamp={1}>{viewingCamera.name || 'Camera'}</Text>
                  </Group>
                  <Group gap={6}>
                    <Badge size="xs" variant="light" color="cyan">{viewingCamera.distance?.toFixed(2)} mi</Badge>
                    <Button size="compact-xs" variant={streamMode === 'video' ? 'filled' : 'subtle'} color="red"
                      onClick={() => setStreamMode('video')} disabled={!streamUrl}>Live</Button>
                    <Button size="compact-xs" variant={streamMode === 'snapshot' ? 'filled' : 'subtle'}
                      onClick={() => setStreamMode('snapshot')}>Snapshot</Button>
                    <Button size="compact-xs" variant="subtle" onClick={() => {
                      if (streamMode === 'snapshot') setCamImageTick(t => t + 1);
                      else fetchCameraStream(viewingCamera).then(url => {
                        if (url && hlsRef.current) hlsRef.current.loadSource(url);
                      }).catch(() => {});
                    }}><IconRefresh size={14} /></Button>
                    <Button size="compact-xs" variant="subtle"
                      onClick={() => panTo(viewingCamera.lat, viewingCamera.lng, 17)}>
                      <IconMapPin size={14} />
                    </Button>
                  </Group>
                </Group>
                <div style={{ height: 360, background: '#000', borderRadius: 6, overflow: 'hidden', position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {streamLoading ? (
                    <Group gap={8}><Loader size={16} color="white" /><Text c="white" size="sm">Loading stream...</Text></Group>
                  ) : streamMode === 'video' && streamUrl ? (
                    <video ref={videoRef} style={{ width: '100%', height: '100%', objectFit: 'contain' }} muted autoPlay playsInline />
                  ) : viewingCamera.imageUrl ? (
                    <img src={`${viewingCamera.imageUrl}${viewingCamera.imageUrl.includes('?') ? '&' : '?'}_t=${camImageTick}`}
                      alt={viewingCamera.name} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                  ) : (
                    <Text c="dimmed" size="sm">No image available</Text>
                  )}
                  <div style={{ position: 'absolute', top: 8, left: 8, display: 'flex', alignItems: 'center', gap: 4, background: 'rgba(0,0,0,0.7)', padding: '2px 8px', borderRadius: 4 }}>
                    <div style={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: streamMode === 'video' && streamUrl ? '#f44336' : '#ffa726' }} />
                    <Text size="xs" c="white" fw={600}>{streamMode === 'video' && streamUrl ? 'LIVE' : 'SNAPSHOT'}</Text>
                  </div>
                </div>
              </div>
            ) : (
              <ScrollArea h={400}>
                {cameras.length === 0 ? (
                  <Text size="sm" c="dimmed" ta="center" py="xl">No cameras nearby.</Text>
                ) : (
                  <SimpleGrid cols={{ base: 2, sm: 3, md: 4 }} spacing="sm">
                    {cameras.map((cam, i) => (
                      <Paper key={i} withBorder p={0} style={{ cursor: 'pointer', overflow: 'hidden', borderRadius: 8 }}
                        onClick={() => handleSelectCamera(cam)}>
                        <div style={{ height: 120, background: '#000', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
                          {cam.imageUrl ? (
                            <AutoRefreshImage src={cam.imageUrl} alt={cam.name}
                              intervalMs={8000}
                              style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                          ) : (
                            <Text size="xs" c="dimmed">No preview</Text>
                          )}
                          <div style={{ position: 'absolute', top: 4, right: 4, background: 'rgba(0,0,0,0.7)', padding: '1px 6px', borderRadius: 4 }}>
                            <Text size="xs" c="white" fw={600}>{cam.distance?.toFixed(1)} mi</Text>
                          </div>
                        </div>
                        <div style={{ padding: '6px 8px' }}>
                          <Text size="xs" fw={600} lineClamp={1}>{cam.name || `Camera ${i + 1}`}</Text>
                          <Badge size="xs" variant="light" mt={2}>{cam.source || '?'}</Badge>
                        </div>
                      </Paper>
                    ))}
                  </SimpleGrid>
                )}
              </ScrollArea>
            )}
          </Tabs.Panel>

          {/* Staging */}
          <Tabs.Panel value="staging">
            <Text size="xs" c="dimmed" mb="sm">
              Large facilities suitable for command posts, staging areas, or shelters.
            </Text>
            <ScrollArea h={380}>
              {stagingAreas.length === 0 ? (
                <Text size="sm" c="dimmed" ta="center" py="xl">{pois.length === 0 ? 'Scan zone first.' : 'No suitable staging areas found.'}</Text>
              ) : (
                <Stack gap={4}>
                  {stagingAreas.map((a: any, i: number) => (
                    <Paper key={i} withBorder px={8} py={6} style={{ cursor: 'pointer' }}
                      onClick={() => panTo(a.lat, a.lng, 16)}>
                      <Group justify="space-between" wrap="nowrap">
                        <Box style={{ flex: 1, minWidth: 0 }}>
                          <Text size="xs" fw={600} lineClamp={1}>{a.name}</Text>
                          <Group gap={4} mt={2}>
                            <Badge size="xs" variant="light">{a.category}</Badge>
                            <Text size="xs" c="dimmed">{(DIR_LABELS as any)[a.direction] || a.direction}</Text>
                          </Group>
                          <Text size="xs" c="dimmed" mt={2}>{a.reason}</Text>
                        </Box>
                        <Stack gap={2} align="flex-end">
                          {a.isOutside && <Badge size="xs" variant="outline" color="green">Outside</Badge>}
                          <Badge size="xs" variant="outline" color="gray">{a.distance.toFixed(2)} mi</Badge>
                        </Stack>
                      </Group>
                    </Paper>
                  ))}
                </Stack>
              )}
            </ScrollArea>
          </Tabs.Panel>

          {/* Evacuation */}
          <Tabs.Panel value="evacuation">
            <ScrollArea h={400}>
              {evacuationRoutes.length === 0 ? (
                <Text size="sm" c="dimmed" ta="center" py="xl">{closures.length === 0 ? 'Scan zone first.' : 'No evacuation routes identified.'}</Text>
              ) : (
                <Stack gap={4}>
                  {evacuationRoutes.slice(0, 20).map((r: any, i: number) => (
                    <Paper key={i} withBorder px={8} py={4} style={{ cursor: 'pointer' }}
                      onClick={() => panTo(r.lat, r.lng, 16)}>
                      <Group justify="space-between" wrap="nowrap">
                        <Box style={{ flex: 1, minWidth: 0 }}>
                          <Text size="xs" fw={500} lineClamp={1}>{r.name}</Text>
                          <Group gap={4}>
                            <Text size="xs" c="dimmed">{(DIR_LABELS as any)[r.direction] || r.direction}</Text>
                            <Text size="xs" c="dimmed">{r.roadType}</Text>
                          </Group>
                        </Box>
                        <Badge size="xs" color={SUITABILITY_COLORS[r.suitability] || 'gray'}>{r.suitability}</Badge>
                      </Group>
                    </Paper>
                  ))}
                </Stack>
              )}
            </ScrollArea>
          </Tabs.Panel>

          {/* Weather */}
          <Tabs.Panel value="weather">
            {weather ? (
              <Paper p="md" withBorder>
                <SimpleGrid cols={3} spacing="md">
                  <div>
                    <Text size="xs" c="dimmed" tt="uppercase">Temperature</Text>
                    <Text size="xl" fw={700}>{weather.temp}°F</Text>
                    <Text size="sm" c="dimmed">{weather.description}</Text>
                  </div>
                  <div>
                    <Text size="xs" c="dimmed" tt="uppercase">Wind</Text>
                    <Text size="xl" fw={700}>{weather.windSpeed} mph</Text>
                    <Text size="sm" c="dimmed">{weather.windDir}</Text>
                  </div>
                  <div>
                    <Text size="xs" c="dimmed" tt="uppercase">Humidity</Text>
                    <Text size="xl" fw={700}>{weather.humidity}%</Text>
                    <Text size="sm" c="dimmed">Vis: {weather.visibility} mi</Text>
                  </div>
                </SimpleGrid>
                {weather.alerts?.length > 0 && (
                  <Paper p="sm" mt="md" withBorder style={{ borderColor: '#F59E0B', background: '#FFFBEB' }}>
                    <Text size="sm" fw={600} c="#92400E">Active Weather Alerts</Text>
                    {weather.alerts.map((a: any, i: number) => (
                      <Text key={i} size="sm" mt={4}>{a.event}: {a.description?.slice(0, 200)}</Text>
                    ))}
                  </Paper>
                )}
              </Paper>
            ) : (
              <Text size="sm" c="dimmed" ta="center" py="xl">Scan zone to fetch weather.</Text>
            )}
          </Tabs.Panel>

          {/* AI Briefing */}
          <Tabs.Panel value="briefing">
            <Group mb="md">
              <Button size="sm" leftSection={<IconBrain size={14} />}
                onClick={handleGenerateBriefing} loading={briefingLoading}
                disabled={pois.length === 0 && closures.length === 0}>
                {briefingText ? 'Regenerate' : 'Generate AI Briefing'}
              </Button>
              {pois.length === 0 && closures.length === 0 && (
                <Text size="xs" c="dimmed">Scan zone first.</Text>
              )}
            </Group>
            {briefingLoading ? (
              <Center py="xl"><Loader size="sm" /><Text size="sm" c="dimmed" ml="sm">Generating...</Text></Center>
            ) : briefingText ? (
              <Paper p="md" withBorder>
                <div style={{ whiteSpace: 'pre-wrap', fontSize: 14, lineHeight: 1.6 }}
                  dangerouslySetInnerHTML={{
                    __html: briefingText.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>').replace(/\n/g, '<br/>'),
                  }} />
              </Paper>
            ) : (
              <Text size="sm" c="dimmed" ta="center" py="xl">Generate a briefing from scan data.</Text>
            )}
          </Tabs.Panel>
        </Tabs>
      )}

      <ZoneForm opened={showEdit} onClose={() => setShowEdit(false)} onSave={handleEditSave}
        initialData={zone} title={`Edit: ${zone.name}`} />
    </div>
  );
}
